import { useMemo } from 'react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis } from 'recharts';
import { startOfWeek, format } from 'date-fns';
import { RiskSignalRow, TrendBucket, chartConfig } from './riskUtils';

// Weekly severity-bucketed trend, scoped to whatever signals are passed in
// (a single project's raw — not deduped — detection history). Raw rows are
// intentional here: each row is a real detection *event*, so counting every
// row (not just the latest per finding) is what makes "how many findings
// fired this week" honest.
export const ProjectRiskTrend = ({ signals }: { signals: RiskSignalRow[] }) => {
  const trendData = useMemo(() => {
    const weekBuckets = new Map<string, TrendBucket>();
    for (const s of signals) {
      const weekStart = startOfWeek(new Date(s.detected_at), { weekStartsOn: 1 });
      const weekKey = weekStart.toISOString();
      if (!weekBuckets.has(weekKey)) {
        weekBuckets.set(weekKey, { week: format(weekStart, 'MMM d'), critical: 0, high: 0, medium: 0, low: 0 });
      }
      weekBuckets.get(weekKey)![s.severity]++;
    }
    return Array.from(weekBuckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, bucket]) => bucket);
  }, [signals]);

  if (trendData.length < 2) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Insufficient history yet — the trend chart needs at least two weeks of detected signals for this project.
      </div>
    );
  }

  return (
    <div className="h-64">
      <ChartContainer config={chartConfig}>
        <LineChart data={trendData}>
          <XAxis dataKey="week" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={{ stroke: 'hsl(var(--border))' }} />
          <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} axisLine={{ stroke: 'hsl(var(--border))' }} allowDecimals={false} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Line type="monotone" dataKey="critical" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ fill: 'hsl(var(--destructive))' }} name="Critical" />
          <Line type="monotone" dataKey="high" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} name="High" />
          <Line type="monotone" dataKey="medium" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: 'hsl(var(--primary))' }} name="Medium" />
          <Line type="monotone" dataKey="low" stroke="hsl(var(--muted-foreground))" strokeWidth={2} dot={{ fill: 'hsl(var(--muted-foreground))' }} name="Low" />
        </LineChart>
      </ChartContainer>
    </div>
  );
};

export default ProjectRiskTrend;
